import { Link } from '@tanstack/react-router';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Logo } from '../../components/brand/Logo';
import { cx } from '../../lib/util/cx';
import type { NavSection } from './nav';

/**
 * Port of the DS `AdminSidebar` + the kit's shell header/footer.
 *
 * 248px ↔ 64px over 400ms ease-in-out. Brown-deep ground, orange uppercase
 * group headings, cream .78 rows; active = orange wash + orange text + 600.
 * Collapsed: labels and headings hide, icons centre, rows get a tooltip.
 */
export function Sidebar({
  sections,
  activeId,
  collapsed,
  onToggle,
}: {
  sections: NavSection[];
  activeId?: string;
  collapsed: boolean;
  onToggle: () => void;
}) {
  return (
    <nav
      aria-label="Admin"
      className={cx(
        'flex shrink-0 flex-col overflow-hidden bg-sidebar font-body text-sidebar-fg',
        'transition-[width] duration-sidebar ease-in-out',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <div
        className={cx(
          'flex items-center gap-2.5 border-b border-sidebar-border py-[18px]',
          collapsed ? 'justify-center px-3' : 'px-5',
        )}
      >
        {collapsed ? (
          <Logo kind="icon" height={30} />
        ) : (
          <>
            <Logo variant="cream" height={22} />
            <span className="text-[11px] font-bold uppercase tracking-[.14em] text-orange">Admin</span>
          </>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-[18px] overflow-y-auto px-2.5 py-3.5">
        {sections.map((section) => (
          <div key={section.label} className="flex flex-col gap-0.5">
            {!collapsed && (
              <div className="px-2.5 pb-1.5 text-[11px] font-bold uppercase tracking-[.14em] text-orange">
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const active = item.id === activeId;
              const Icon = item.icon;
              return (
                <Link
                  key={item.id}
                  to={item.path}
                  title={collapsed ? item.label : undefined}
                  aria-current={active ? 'page' : undefined}
                  className={cx(
                    'flex w-full items-center gap-2.5 rounded-app text-sm no-underline transition-[background-color,color] duration-fast',
                    collapsed ? 'justify-center px-0 py-2.5' : 'px-2.5 py-[9px]',
                    active
                      ? 'bg-sidebar-active-bg font-semibold text-sidebar-active-fg hover:text-sidebar-active-fg'
                      : 'font-medium text-[rgba(241,237,225,.78)] hover:bg-sidebar-hover-bg hover:text-[rgba(241,237,225,.78)]',
                  )}
                >
                  <Icon size={17} strokeWidth={2} aria-hidden="true" className="shrink-0" />
                  {!collapsed && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
                </Link>
              );
            })}
          </div>
        ))}
      </div>

      <div
        className={cx(
          'flex items-center gap-2 border-t border-sidebar-border text-xs text-sidebar-fg-muted',
          collapsed ? 'justify-center p-3' : 'justify-between px-5 py-3.5',
        )}
      >
        {!collapsed && <span>v{import.meta.env.VITE_VERSION}</span>}
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!collapsed}
          className="flex h-7 w-7 items-center justify-center rounded-app border-0 bg-[rgba(241,237,225,.08)] text-cream active:opacity-80"
        >
          {collapsed ? <ChevronsRight size={16} aria-hidden="true" /> : <ChevronsLeft size={16} aria-hidden="true" />}
        </button>
      </div>
    </nav>
  );
}
